/**
 * Thin fetch wrappers around the REST `/api/auth/*` endpoints.
 *
 * These exist (rather than going through tRPC) because the .NET system
 * shapes login/register/forgot/reset as REST so the same URL paths can be
 * preserved during dual-run cutover (M20 — App-Review URLs cannot move).
 * The session cookie is set by the server response; the client never sees
 * it directly.
 */
async function postJson(path: string, body: unknown) {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { ok: false, error: text || 'Unexpected response' };
  }
  if (!res.ok) {
    const j = json as { error?: string; code?: string };
    throw Object.assign(new Error(j.error ?? `Request failed (${res.status})`), {
      code: j.code,
      status: res.status,
    });
  }
  return json as { ok: true };
}

export const authApi = {
  login: (email: string, password: string) => postJson('/api/auth/login', { email, password }),
  register: (input: {
    email: string;
    password: string;
    vendorTitle: string;
    firstName?: string;
    lastName?: string;
    mobileNumber?: string;
  }) => postJson('/api/auth/register', input),
  logout: () => postJson('/api/auth/logout', {}),
  forgotPassword: (email: string) => postJson('/api/auth/forgot-password', { email }),
  resetPassword: (input: {
    email: string;
    token: string;
    password: string;
    passwordConfirmation: string;
  }) => postJson('/api/auth/reset-password', input),
  facebookLoginUrl: '/api/auth/facebook-login',
  logoutAsUrl: '/api/auth/logout-as',
};
